import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pane, Button, Spinner, Text, SelectField } from 'evergreen-ui';
import { generateMensialePdf, calcMonthlyStats } from '../../utils/generateTurniPdf';
import { fetchTurniByMonth, fetchFerieRequests } from '../../services/turniService';
import useWindowSize from '../../utils/useWindowSize';

export default function TurniPdfPreviewModal({
  isOpen,
  onClose,
  allDays,
  collaboratorsList,
  turniData,
  managerFerieList,
}) {
  const [selectedId, setSelectedId] = useState('tutti');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const prevUrlRef = useRef(null);
  const width = useWindowSize();
  const isMobile = width < 768;

  useEffect(() => {
    if (!isOpen) return;

    const filtered = selectedId === 'tutti'
      ? collaboratorsList
      : collaboratorsList.filter(u => String(u.id) === selectedId);

    if (filtered.length === 0) return;

    setLoading(true);
    const timerId = setTimeout(async () => {
      try {
        // Determina anno e mese corrente dai giorni passati
        const selectedMonthStr = allDays[0]?.fullDate?.substring(0, 7);
        const [year, monthNum] = selectedMonthStr
          ? selectedMonthStr.split('-').map(Number)
          : [new Date().getFullYear(), new Date().getMonth() + 1];

        // Fetch turni da gennaio al mese corrente (in parallelo) + tutte le ferie
        const monthsKeys = Array.from({ length: monthNum }, (_, i) =>
          `${year}-${String(i + 1).padStart(2, '0')}`
        );
        const [allMonthsTurni, allFerie] = await Promise.all([
          Promise.all(
            monthsKeys.map(m =>
              fetchTurniByMonth(m).then(data => [m, data || []]).catch(() => [m, []])
            )
          ).then(entries => Object.fromEntries(entries)),
          fetchFerieRequests().catch(() => []),
        ]);

        // Calcola saldo cumulativo (da gennaio al mese corrente) per ogni collaboratore
        const cumulativeBalances = {};
        filtered.forEach(user => {
          const uName = `${user.nome} ${user.cognome}`.trim();
          let cumulo = 0;
          monthsKeys.forEach(monthStr => {
            const [y, m] = monthStr.split('-').map(Number);
            const daysInMonth = new Date(y, m, 0).getDate();
            const monthDays = Array.from({ length: daysInMonth }, (_, i) => ({
              fullDate: `${monthStr}-${String(i + 1).padStart(2, '0')}`,
            }));
            const stats = calcMonthlyStats(monthDays, allMonthsTurni[monthStr] || [], allFerie, user);
            const effettive = stats.workedHours + stats.ferieHours + stats.permessiHours + stats.malattiaHours;
            cumulo += effettive - stats.oreMensiliContratto;
          });
          cumulativeBalances[uName] = +Number(cumulo).toFixed(1);
        });

        const doc = generateMensialePdf({
          allDays,
          collaboratorsList: filtered,
          turniData,
          managerFerieList,
          cumulativeBalances,
        });
        const blob = doc.output('blob');
        const url = URL.createObjectURL(blob);
        if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current);
        prevUrlRef.current = url;
        setPdfUrl(url);
      } catch (e) {
        console.error('Errore generazione PDF', e);
      } finally {
        setLoading(false);
      }
    }, 60);

    return () => clearTimeout(timerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedId]);

  useEffect(() => {
    if (!isOpen) {
      if (prevUrlRef.current) {
        URL.revokeObjectURL(prevUrlRef.current);
        prevUrlRef.current = null;
      }
      setPdfUrl(null);
      setSelectedId('tutti');
    }
  }, [isOpen]);

  // Blocca scroll body quando il modal è aperto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleDownload = async () => {
    const filtered = selectedId === 'tutti'
      ? collaboratorsList
      : collaboratorsList.filter(u => String(u.id) === selectedId);

    const selectedMonthStr = allDays[0]?.fullDate?.substring(0, 7);
    const [year, monthNum] = selectedMonthStr
      ? selectedMonthStr.split('-').map(Number)
      : [new Date().getFullYear(), new Date().getMonth() + 1];

    const monthsKeys = Array.from({ length: monthNum }, (_, i) =>
      `${year}-${String(i + 1).padStart(2, '0')}`
    );
    const [allMonthsTurni, allFerie] = await Promise.all([
      Promise.all(
        monthsKeys.map(m =>
          fetchTurniByMonth(m).then(data => [m, data || []]).catch(() => [m, []])
        )
      ).then(entries => Object.fromEntries(entries)),
      fetchFerieRequests().catch(() => []),
    ]);

    const cumulativeBalances = {};
    filtered.forEach(user => {
      const uName = `${user.nome} ${user.cognome}`.trim();
      let cumulo = 0;
      monthsKeys.forEach(monthStr => {
        const [y, m] = monthStr.split('-').map(Number);
        const daysInMonth = new Date(y, m, 0).getDate();
        const monthDays = Array.from({ length: daysInMonth }, (_, i) => ({
          fullDate: `${monthStr}-${String(i + 1).padStart(2, '0')}`,
        }));
        const stats = calcMonthlyStats(monthDays, allMonthsTurni[monthStr] || [], allFerie, user);
        const effettive = stats.workedHours + stats.ferieHours + stats.permessiHours + stats.malattiaHours;
        cumulo += effettive - stats.oreMensiliContratto;
      });
      cumulativeBalances[uName] = +Number(cumulo).toFixed(1);
    });

    const doc = generateMensialePdf({ allDays, collaboratorsList: filtered, turniData, managerFerieList, cumulativeBalances });
    const mese = selectedMonthStr || 'mese';
    const suffisso = selectedId === 'tutti'
      ? 'tutti'
      : (filtered[0] ? `${filtered[0].nome}_${filtered[0].cognome}` : 'collaboratore');
    doc.save(`turni_${mese}_${suffisso}.pdf`);
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 500,
        }}
      />

      {/* Finestra ridimensionabile
          position:fixed + inset:0 + margin:auto = centrata automaticamente.
          resize:both aggiunge il cursore di resize nell'angolo in basso a destra. */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          margin: 'auto',
          width: isMobile ? '99vw' : 'min(1500px, 99vw)',
          height: isMobile ? '95vh' : '94vh',
          minWidth: 280,
          minHeight: 300,
          maxWidth: '99vw',
          maxHeight: '97vh',
          resize: isMobile ? 'none' : 'both',
          overflow: 'hidden',
          background: 'white',
          borderRadius: 8,
          boxShadow: '0 8px 40px rgba(0,0,0,0.35)',
          zIndex: 501,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Barra titolo */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '10px 16px',
          borderBottom: '1px solid #E6E8F0',
          background: '#F9F9FB',
          flexShrink: 0,
          userSelect: 'none',
        }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: '#101840' }}>
            Anteprima PDF — Resoconto mensile turni
          </span>
          <button
            onClick={onClose}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 18,
              lineHeight: 1,
              color: '#696F8C',
              padding: '0 4px',
            }}
            aria-label="Chiudi"
          >
            ✕
          </button>
        </div>

        {/* Barra strumenti */}
        <Pane
          display="flex"
          alignItems={isMobile ? 'flex-start' : 'center'}
          flexDirection={isMobile ? 'column' : 'row'}
          gap={isMobile ? 8 : 16}
          padding={12}
          borderBottom="default"
          background="tint2"
          flexShrink={0}
        >
          <SelectField
            label="Collaboratore"
            value={selectedId}
            onChange={e => setSelectedId(e.target.value)}
            marginBottom={0}
            width={isMobile ? '100%' : 230}
          >
            <option value="tutti">Tutti i collaboratori</option>
            {collaboratorsList.map(u => (
              <option key={u.id} value={String(u.id)}>
                {u.nome} {u.cognome}
              </option>
            ))}
          </SelectField>

          {!isMobile && (
            <Text size={300} color="muted">
              {allDays.length > 0
                ? `Mese: ${new Date(`${allDays[0].fullDate}T12:00:00`).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}`
                : ''}
            </Text>
          )}

          <Pane flex={1} />

          <Pane display="flex" gap={8} width={isMobile ? '100%' : 'auto'}>
            <Button onClick={onClose} flex={isMobile ? 1 : undefined}>Chiudi</Button>
            <Button appearance="primary" onClick={handleDownload} disabled={loading} flex={isMobile ? 1 : undefined}>
              Scarica PDF
            </Button>
          </Pane>
        </Pane>

        {/* Area anteprima — flex:1 riempie lo spazio residuo dopo titolo+toolbar */}
        <div style={{ flex: 1, position: 'relative', background: isMobile ? '#f9f9fb' : '#555', overflow: 'hidden' }}>
          {loading && (
            <Pane
              position="absolute"
              top={0} left={0} right={0} bottom={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              background="rgba(255,255,255,0.88)"
              zIndex={1}
            >
              <Spinner />
              <Text marginLeft={12} color="muted">Generazione PDF in corso...</Text>
            </Pane>
          )}

          {isMobile ? (
            !loading && (
              <Pane
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100%"
                gap={16}
                padding={24}
              >
                <Text size={400} textAlign="center">
                  L'anteprima non è disponibile su dispositivi mobili.
                </Text>
                <Text size={300} color="muted" textAlign="center">
                  Usa il bottone "Scarica PDF" per aprire il documento.
                </Text>
                <Button appearance="primary" size="large" onClick={handleDownload}>
                  Scarica PDF
                </Button>
              </Pane>
            )
          ) : (
            pdfUrl && !loading && (
              <iframe
                src={pdfUrl}
                title="Anteprima PDF turni"
                style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
              />
            )
          )}
        </div>
      </div>
    </>,
    document.body
  );
}
